import { getNode, setupResizeHandler, animate } from './utils';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css'; // optional for styling
import 'tippy.js/themes/light.css'; // optional for styling

const template = `
  <div class="tsg-table">
    <div class="tsg-table-wrapper">
      <table cellspacing="0">
        <thead></thead>
        <tbody></tbody>
        <tfoot></tfoot>
      </table>
    </div>
  </div>
`;


class Table {
  constructor(containerOrSelector, options = {}) {
    this.container = getNode(containerOrSelector);
    this.options = {
      ...options,
    };

    this.data = this.options.data;
    this.columns = this.options.columns;

    this.setupHTML();
    this.putData();

    setupResizeHandler(this.container, 600, (mobile) => { this.doResize(mobile); }, 200);

    this.setupSticky();

    this.setupScrollHint();
  }

  setupHTML() {
    this.container.innerHTML = template;
    this.elems = {
      main: this.container.querySelector('.tsg-table'),
      wrapper: this.container.querySelector('.tsg-table-wrapper'),
      table: this.container.querySelector('table'),
      head: this.container.querySelector('thead'),
      body: this.container.querySelector('tbody'),
      foot: this.container.querySelector('tfoot'),
    };
  }


  putData() {
    const columnLabels = {};
    this.columns.forEach((c, i) => { columnLabels[i] = c.label; });

    this.addRow(
      columnLabels,
      this.columns.map((x, i) => ({
        align: x.align, key: i, noValue: x.noHeader, index: x.index,
        tooltip: x.headerTooltip,
      })),
      this.elems.head,
    );

    this.data.forEach((d, i) => {
      this.addRow(d, this.columns, this.elems.body, i % 2 === 0, i === 0);
    });
  }

  addRow(d, columns, elem, striped, isFirst) {
    const row = document.createElement('tr');
    row.classList.add('tsg-table-row');

    if (this.options.highlight) {
      const indexCol = columns.find(c => c.index);
      const indexKey = indexCol ? indexCol.key : null;
      if (!!indexKey && d[indexKey] === this.options.highlight) {
        row.style.backgroundColor = this.options.highlightColor;
        row.classList.add('tsg-table-row--highlight');
      }
    }

    columns.forEach((c, i) => {
      const cell = document.createElement('td');
      cell.classList.add('tsg-table-cell');
      if (c.align === 'center') {
        cell.classList.add('tsg-table-cell--center');
      } else if (c.align === 'right') {
        cell.classList.add('tsg-table-cell--right');
      } else {
        cell.classList.add('tsg-table-cell--left');
      }
      if (i === 0) cell.classList.add('tsg-table-cell--first');
      if (i === columns.length - 1) cell.classList.add('tsg-table-cell--last');
      if (c.index) {
        cell.classList.add('tsg-table-cell--index');
        cell.style.color = this.options.indexTextColor;
      }
      cell.style.backgroundColor = this.options.backgroundColor || '#fff';
      if (this.options.backgroundColorStripes && striped) {
        cell.style.backgroundColor = this.options.backgroundColorStripes;
      }

      const value = d[c.key];
      if (c.color) cell.style.backgroundColor = c.color(value);
      if (c.circle) {
        const ccircle = document.createElement('div');
        ccircle.classList.add('tsg-table-cell__circle');
        if (c.circleColor) ccircle.style.backgroundColor = c.circleColor(value);
        if (c.circleSymbol) {
          const sym = c.circleSymbol(value);
          if (sym) {
            const csym = document.createElement('div');
            csym.classList.add('tsg-table-cell__circle-symbol');
            csym.classList.add(`tsg-table-cell__circle-symbol--${sym}`);
            ccircle.appendChild(csym);
          }
        }
        if (c.circleNote) {
          const note = c.circleNote(value);
          if (note) {
            const cnote = document.createElement('div');
            cnote.classList.add('tsg-table-cell__circle-note');
            cnote.innerHTML = note;
            ccircle.appendChild(cnote);
          }
        }

        cell.appendChild(ccircle);
      }

      if (c.text) {
        const ctext = document.createElement('div');
        ctext.classList.add('tsg-table-cell__text');
        if (c.bold) ctext.style.fontWeight = '500';
        ctext.append(c.formatFunction ? c.formatFunction(value) : value);
        if (c.textColor) ctext.style.color = c.textColor(value);
        cell.appendChild(ctext);
      }

      if (!c.noValue) {
        const cvalue = document.createElement('div');
        cvalue.classList.add('tsg-table-cell__value');
        cvalue.innerHTML = c.formatFunction ? c.formatFunction(value) : value;
        cell.appendChild(cvalue);
      }
      if (c.graphElem) {
        const graph = c.graphElem(d, isFirst);
        cell.appendChild(graph);
      }
      row.appendChild(cell);

      let tooltip;

      if (c.tooltipTextFormatFunc || c.valueTooltip) {
        const indexKey = columns[0].key;
        if (c.tooltipTextFormatFunc || d[c.valueTooltip]) {
          const text = c.tooltipTextFormatFunc ? c.tooltipTextFormatFunc(d) : `${d[indexKey]}: ${d[c.valueTooltip]}`;
          tooltip = text;
        }
      }

      if (tooltip || c.tooltip) {
        cell.classList.add('tsg-table-cell--tooltip');
        tippy(cell, {
          content: `<div class="tsg-table-tooltip euroviz-font-graphic">${tooltip || c.tooltip}</div>`,
          allowHTML: true,
          maxWidth: 260,
          theme: 'light',
          arrow: true,
          placement: 'bottom',
          duration: 0,
        });
      }
    });
    elem.appendChild(row);
  }

  doResize(mobile) {
    this.mobile = mobile;
    this.canScroll = this.elems.table.getBoundingClientRect().width
      > this.elems.main.getBoundingClientRect().width;
    this.elems.main.classList.toggle('tsg-table--mobile', this.mobile);
    this.elems.main.classList.toggle('tsg-table--scroll', this.canScroll);
  }

  setupSticky() {
    if (this.options.headerSticky) this.elems.main.classList.add('tsg-table--header-sticky');
    if (this.options.indexSticky) {
      this.elems.main.classList.add('tsg-table--index-sticky');

      /*
      const firstRow = this.elems.body.querySelector('tr');
      const sentinel = document.createElement('div');
      sentinel.classList.add('tsg-table-sticky-sentinel');
      firstRow.appendChild(sentinel);

      const observer = new IntersectionObserver((records) => {
        if (records.length) {
          if (records[0].intersectionRatio < 1) {
            this.elems.main.classList.add('tsg-table--index-sticking');
          } else {
            this.elems.main.classList.remove('tsg-table--index-sticking');
          }
        }
      }, { threshold: [1], root: this.elems.table.parentNode });

      observer.observe(sentinel);


      const sentinelR = document.createElement('div');
      sentinelR.classList.add('tsg-table-sticky-sentinel-right');
      firstRow.appendChild(sentinelR);

      const observerR = new IntersectionObserver((records) => {
        if (records.length) {
          if (records[0].intersectionRatio < 1) {
            this.elems.main.classList.add('tsg-table--overflow-right');
          } else {
            this.elems.main.classList.remove('tsg-table--overflow-right');
          }
        }
      }, { threshold: [1], root: this.elems.table.parentNode });

      observerR.observe(sentinelR);
      */
      this.elems.wrapper.addEventListener('scroll', () => {
        if (this.elems.wrapper.scrollLeft > 0) {
          this.elems.main.classList.add('tsg-table--index-sticking');
        } else {
          this.elems.main.classList.remove('tsg-table--index-sticking');
        }

        const scrollable = this.elems.table.clientWidth > this.elems.wrapper.clientWidth;
        const scrolling = this.elems.wrapper.scrollLeft
          < this.elems.table.clientWidth - this.elems.wrapper.clientWidth - 1;
        if (scrollable && scrolling) {
          this.elems.main.classList.add('tsg-table--overflow-right');
        } else {
          this.elems.main.classList.remove('tsg-table--overflow-right');
        }
      });
    }
  }

  setupScrollHint() {
    const observer = new IntersectionObserver((records) => {
      if (records.length) {
        if (records[0].intersectionRatio > 0 && this.canScroll) {
          setTimeout(() => {
            animate(0, 50, 200, (v) => { this.elems.wrapper.scrollLeft = v; });
          }, 400);
          setTimeout(() => {
            animate(50, 0, 200, (v) => { this.elems.wrapper.scrollLeft = v; });
          }, 600);
          observer.unobserve(this.elems.main);
        }
      }
    }, { threshold: [0.3] });

    observer.observe(this.elems.main);
  }
}


export default Table;
